
// @ts-nocheck
import { supabase } from "@/integrations/supabase/client";
import { PostgrestError, SupabaseClient } from "@supabase/supabase-js";

export type Tables<T extends keyof Database["public"]["Tables"]> = 
  Database["public"]["Tables"][T]["Row"];

export type TableInsert<T extends keyof Database["public"]["Tables"]> = 
  Database["public"]["Tables"][T]["Insert"];

export type TableUpdate<T extends keyof Database["public"]["Tables"]> = 
  Database["public"]["Tables"][T]["Update"];

/**
 * Helper function to check if an error is a Supabase PostgrestError
 */
export const isSupabaseError = (error: unknown): error is PostgrestError => {
  return Boolean(
    error && typeof error === 'object' && 'code' in error && 'message' in error
  );
};

/**
 * Safely converts Supabase data to an array, handling null/undefined cases
 */
export const safeDataArray = <T>(data: T[] | null | undefined): T[] => {
  return data || [];
};

/**
 * Maps data from Supabase query to the desired format
 * Updated to use a single parameter when no mapping function is provided
 */
export function mapSupabaseData<T, R = T>(data: T[] | null): T[];
export function mapSupabaseData<T, R = T>(data: T[] | null, mapFn: (item: T) => R): R[];
export function mapSupabaseData<T, R = T>(data: T[] | null, mapFn?: (item: T) => R): T[] | R[] {
  if (!data) return [];
  if (!mapFn) return data;
  return data.map(mapFn);
}

/**
 * Get currently live broadcast if available
 */
export const getLiveBroadcast = async () => {
  const { data, error } = await supabase
    .from('broadcasts')
    .select('*')
    .eq('is_live', true)
    .maybeSingle();

  if (error) {
    console.error("Error fetching live broadcast:", error);
    return null;
  }

  return data;
};

/**
 * Get currently live auction if available
 */
export const getLiveAuction = async () => {
  const { data, error } = await supabase
    .from('auctions')
    .select('*')
    .eq('is_live', true)
    .maybeSingle();

  if (error) {
    console.error("Error fetching live auction:", error);
    return null;
  }

  return data;
};

/**
 * Helper to adapt database types for Consignment
 * This is useful for ConsignmentManager where types might not match exactly
 */
export const adaptConsignmentData = <T extends { additional_images: any }>(data: T[]): any[] => {
  return data.map(item => ({
    ...item,
    // Convert additional_images to array if it's not already
    additional_images: Array.isArray(item.additional_images) 
      ? item.additional_images 
      : (item.additional_images ? [item.additional_images] : [])
  }));
};

/**
 * Check if user is an admin based on their ID
 */
export const isUserAdmin = async (userId: string | undefined) => {
  if (!userId) return false;
  
  try {
    const { data, error } = await supabase.rpc('is_admin', { user_id: userId });
    if (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
    return !!data; 
  } catch (error) {
    console.error("Failed to check admin status:", error);
    return false;
  }
};
